import { X } from 'lucide-react-native';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import Svg, { Circle, Path } from 'react-native-svg';

import { IdentityHeader } from '@/src/components/IdentityHeader';
import { useToast } from '@/src/components/Toast';
import {
  FRIENZZI_BOT_NAME,
  FRIENZZI_BOT_UID,
} from '@/src/features/bot/bot-identity';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { openUserProfile } from '@/src/utils/profile-navigation';

import {
  emptyProfileReactionCounts,
  totalProfileReactions,
  type ProfileConnectionKind,
  type ProfileConnection,
  type ProfileReaction,
  type ProfileStats,
} from './profile-service';
import {
  useFollowState,
  useProfileConnectionSearch,
  useProfileConnections,
  useToggleFollow,
} from './use-profile';

const REACTION_ORDER: ProfileReaction[] = [
  'love',
  'like',
  'dislike',
  'laugh',
  'wow',
  'vomit',
  'fire',
  'trash',
];

const REACTION_META: Record<
  ProfileReaction,
  { color: string; emoji: string; label: string }
> = {
  love: { color: '#F72585', emoji: '❤️', label: 'Love' },
  like: { color: '#7C66C4', emoji: '👍', label: 'Like' },
  dislike: { color: '#6B7280', emoji: '👎', label: 'Dislike' },
  laugh: { color: '#FFB703', emoji: '😂', label: 'Laugh' },
  wow: { color: '#4F9CF9', emoji: '😮', label: 'Wow' },
  vomit: { color: '#84CC16', emoji: '🤮', label: 'Vomit' },
  fire: { color: '#FB8500', emoji: '🔥', label: 'Fire' },
  trash: { color: '#111827', emoji: '🗑️', label: 'Trash' },
};

function piePoint(cx: number, cy: number, radius: number, angle: number) {
  const radians = ((angle - 90) * Math.PI) / 180;

  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
}

function piePath({
  cx,
  cy,
  endAngle,
  radius,
  startAngle,
}: {
  cx: number;
  cy: number;
  endAngle: number;
  radius: number;
  startAngle: number;
}) {
  const start = piePoint(cx, cy, radius, startAngle);
  const end = piePoint(cx, cy, radius, endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${cx} ${cy}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`,
    'Z',
  ].join(' ');
}

function ReactionPieChart({ stats }: { stats: ProfileStats }) {
  const size = 180;
  const center = size / 2;
  const radius = 70;
  const total = totalProfileReactions(stats);
  let cursor = 0;

  if (total <= 0) {
    return (
      <View style={styles.chartWrap}>
        <Svg height={size} width={size}>
          <Circle
            cx={center}
            cy={center}
            fill={colors.surfaceTint}
            r={radius}
            stroke={colors.border}
            strokeWidth={2}
          />
        </Svg>
        <View style={styles.chartCenter}>
          <Text style={styles.chartTotal}>0</Text>
          <Text style={styles.chartLabel}>Reactions</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.chartWrap}>
      <Svg height={size} width={size}>
        {REACTION_ORDER.map((reaction) => {
          const count = stats.reactionCounts[reaction] ?? 0;

          if (count <= 0) {
            return null;
          }

          if (count === total) {
            return (
              <Circle
                key={reaction}
                cx={center}
                cy={center}
                fill={REACTION_META[reaction].color}
                r={radius}
              />
            );
          }

          const startAngle = cursor;
          const endAngle = cursor + (count / total) * 360;
          cursor = endAngle;

          return (
            <Path
              key={reaction}
              d={piePath({
                cx: center,
                cy: center,
                endAngle,
                radius,
                startAngle,
              })}
              fill={REACTION_META[reaction].color}
            />
          );
        })}
        <Circle cx={center} cy={center} fill={colors.surface} r={44} />
      </Svg>
      <View style={styles.chartCenter}>
        <Text style={styles.chartTotal}>{total}</Text>
        <Text style={styles.chartLabel}>Reactions</Text>
      </View>
    </View>
  );
}

function ModalShell({
  children,
  onClose,
  title,
  visible,
}: {
  children: ReactNode;
  onClose: () => void;
  title: string;
  visible: boolean;
}) {
  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable
          style={styles.sheet}
          onPress={(event) => event.stopPropagation()}
        >
          <View style={styles.header}>
            <View style={styles.headerSpacer} />
            <Text style={styles.title}>{title}</Text>
            <Pressable
              accessibilityLabel="Close"
              accessibilityRole="button"
              onPress={onClose}
              style={styles.closeButton}
            >
              <X color={colors.text} size={22} strokeWidth={2.5} />
            </Pressable>
          </View>
          {children}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function BotUnfollowConfirmModal({
  onCancel,
  onConfirm,
  visible,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  visible: boolean;
}) {
  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onCancel}
    >
      <Pressable style={styles.confirmBackdrop} onPress={onCancel}>
        <Pressable
          style={styles.confirmSheet}
          onPress={(event) => event.stopPropagation()}
        >
          <Text style={styles.confirmTitle}>Unfollow {FRIENZZI_BOT_NAME}?</Text>
          <Text style={styles.confirmText}>
            You will stop seeing Frienzzi Bot updates in your Frienzzone feed.
            You can follow Frienzzi Bot again anytime.
          </Text>
          <View style={styles.confirmActions}>
            <Pressable
              accessibilityRole="button"
              onPress={onCancel}
              style={[styles.compactActionButton, styles.secondaryActionButton]}
            >
              <Text
                style={[
                  styles.compactActionText,
                  styles.secondaryActionButtonText,
                ]}
              >
                Cancel
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              onPress={onConfirm}
              style={[styles.compactActionButton, styles.dangerActionButton]}
            >
              <Text style={styles.compactActionText}>Unfollow</Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function ConnectionAction({
  currentUid,
  item,
  kind,
  onChanged,
  onRequestBotUnfollow,
}: {
  currentUid?: string;
  item: ProfileConnection;
  kind: ProfileConnectionKind | null;
  onChanged: (change: { following: boolean; uid: string }) => void;
  onRequestBotUnfollow: (action: () => void) => void;
}) {
  const followState = useFollowState(currentUid, item.uid);
  const toggleFollow = useToggleFollow(currentUid, item.uid);
  const { showError, showSuccess } = useToast();
  const followActionPending = followState.loading || toggleFollow.isPending;

  if (!currentUid || currentUid === item.uid || !kind || kind === 'invites') {
    return null;
  }

  const runFollowMutation = (following: boolean) => {
    if (followActionPending) {
      return;
    }

    onChanged({ following, uid: item.uid });

    toggleFollow.mutate(
      { following },
      {
        onSuccess: (result) => {
          if (result === 'queued') {
            showSuccess(`${following ? 'Follow' : 'Unfollow'} queued.`);
            return;
          }

          showSuccess(
            following
              ? `You are now following ${item.fullName}.`
              : `You unfollowed ${item.fullName}.`,
            following ? 'Following' : 'Unfollowed',
          );
        },
        onError: (error) => {
          onChanged({ following: !following, uid: item.uid });
          showError(
            error,
            following
              ? 'Unable to follow this profile.'
              : 'Unable to unfollow this profile.',
          );
        },
      },
    );
  };

  if (kind === 'followers') {
    if (followActionPending) {
      return <ActivityIndicator color={colors.primary} size="small" />;
    }

    if (followState.following) {
      return (
        <Pressable
          accessibilityLabel={`Unfollow ${item.fullName}`}
          accessibilityRole="button"
          onPress={(event) => {
            event.stopPropagation();

            if (item.uid === FRIENZZI_BOT_UID) {
              onRequestBotUnfollow(() => runFollowMutation(false));
              return;
            }

            runFollowMutation(false);
          }}
          style={({ pressed }) => [
            styles.compactActionButton,
            styles.secondaryActionButton,
            pressed ? styles.secondaryActionButtonPressed : null,
          ]}
        >
          <Text
            style={[styles.compactActionText, styles.secondaryActionButtonText]}
          >
            Unfollow
          </Text>
        </Pressable>
      );
    }

    return (
      <Pressable
        accessibilityLabel={`Follow ${item.fullName}`}
        accessibilityRole="button"
        onPress={(event) => {
          event.stopPropagation();
          runFollowMutation(true);
        }}
        style={({ pressed }) => [
          styles.compactActionButton,
          styles.primaryActionButton,
          pressed ? styles.primaryActionButtonPressed : null,
        ]}
      >
        <Text style={styles.compactActionText}>Follow</Text>
      </Pressable>
    );
  }

  return (
    <Pressable
      accessibilityLabel={`Unfollow ${item.fullName}`}
      accessibilityRole="button"
      disabled={followActionPending}
      onPress={(event) => {
        event.stopPropagation();

        if (item.uid === FRIENZZI_BOT_UID) {
          onRequestBotUnfollow(() => runFollowMutation(false));
          return;
        }

        runFollowMutation(false);
      }}
      style={({ pressed }) => [
        styles.compactActionButton,
        styles.secondaryActionButton,
        pressed && !followActionPending
          ? styles.secondaryActionButtonPressed
          : null,
        followActionPending ? styles.compactActionButtonDisabled : null,
      ]}
    >
      <Text
        style={[styles.compactActionText, styles.secondaryActionButtonText]}
      >
        Unfollow
      </Text>
    </Pressable>
  );
}

function ConnectionRow({
  currentUid,
  isOwnProfile,
  item,
  kind,
  onChanged,
  onClose,
  onRequestBotUnfollow,
}: {
  currentUid?: string;
  isOwnProfile: boolean;
  item: ProfileConnection;
  kind: ProfileConnectionKind | null;
  onChanged: (change: { following: boolean; uid: string }) => void;
  onClose: () => void;
  onRequestBotUnfollow: (action: () => void) => void;
}) {
  return (
    <Pressable
      accessibilityLabel={`View ${item.fullName}'s profile`}
      accessibilityRole="button"
      disabled={!currentUid}
      onPress={() => {
        openUserProfile({ currentUid, targetUid: item.uid });
        onClose();
      }}
      style={({ pressed }) => [
        styles.connectionRow,
        pressed ? styles.connectionRowPressed : null,
      ]}
    >
      <IdentityHeader
        avatarSize={40}
        containerStyle={styles.connectionIdentity}
        currentUid={currentUid}
        density="compact"
        founderBadge={item.founderBadge}
        fullName={item.fullName}
        interactive={false}
        photoURL={item.photoURL}
        uid={item.uid}
        username={item.username}
        verifiedBadge={item.verifiedBadge}
      />
      {isOwnProfile ? (
        <ConnectionAction
          currentUid={currentUid}
          item={item}
          kind={kind}
          onChanged={onChanged}
          onRequestBotUnfollow={onRequestBotUnfollow}
        />
      ) : null}
    </Pressable>
  );
}

export function ProfileConnectionsModal({
  currentUid,
  kind,
  onClose,
  profileUid,
  totalCount,
  visible,
}: {
  currentUid?: string;
  kind: ProfileConnectionKind | null;
  onClose: () => void;
  profileUid: string | undefined;
  totalCount: number;
  visible: boolean;
}) {
  const connectionsQuery = useProfileConnections(
    profileUid,
    kind ?? undefined,
    visible,
  );
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [botUnfollowAction, setBotUnfollowAction] = useState<
    (() => void) | null
  >(null);
  const [locallyHiddenUids, setLocallyHiddenUids] = useState<Set<string>>(
    () => new Set(),
  );
  const title =
    kind === 'following'
      ? 'Following'
      : kind === 'invites'
        ? 'Invites'
        : 'Followers';
  const connectionLabel =
    kind === 'following'
      ? 'following'
      : kind === 'invites'
        ? 'invites'
        : 'followers';
  const emptyText =
    kind === 'following'
      ? 'This user is not following anyone yet.'
      : kind === 'invites'
        ? 'No accepted invites yet.'
        : 'No followers yet.';
  const loadedConnections = useMemo(
    () => connectionsQuery.data ?? [],
    [connectionsQuery.data],
  );
  const normalizedSearch = searchText.trim().toLowerCase();
  const isSearching = normalizedSearch.length > 0;
  const searchQuery = useProfileConnectionSearch({
    enabled: visible && isSearching,
    kind: kind ?? undefined,
    searchText: debouncedSearchText,
    uid: profileUid,
  });
  const visibleConnections = useMemo(() => {
    const sourceConnections = isSearching
      ? (searchQuery.data?.connections ?? [])
      : loadedConnections;

    if (locallyHiddenUids.size === 0) {
      return sourceConnections;
    }

    return sourceConnections.filter(
      (connection) => !locallyHiddenUids.has(connection.uid),
    );
  }, [
    isSearching,
    loadedConnections,
    locallyHiddenUids,
    searchQuery.data?.connections,
  ]);
  const totalConnectionCount = Math.max(
    0,
    totalCount - locallyHiddenUids.size,
    loadedConnections.length - locallyHiddenUids.size,
  );
  const loadedCount = Math.max(
    0,
    loadedConnections.length - locallyHiddenUids.size,
  );
  const loadedStatus = `${loadedCount} of ${totalConnectionCount} loaded`;
  const searchScopeStatus = `All ${totalConnectionCount} ${connectionLabel}`;
  const isSearchSettled =
    debouncedSearchText.trim().toLowerCase() === normalizedSearch;
  const isSearchLoading =
    isSearching && (!isSearchSettled || searchQuery.isFetching);
  const searchTotalMatches = Math.max(
    0,
    (searchQuery.data?.totalMatches ?? visibleConnections.length) -
      locallyHiddenUids.size,
  );
  const searchStatus = isSearching
    ? isSearchLoading
      ? 'Searching...'
      : `${visibleConnections.length}${
          searchTotalMatches > visibleConnections.length
            ? ` of ${searchTotalMatches}`
            : ''
        } ${searchTotalMatches === 1 ? 'match' : 'matches'}`
    : null;
  const isOwnProfile = Boolean(
    currentUid && profileUid && currentUid === profileUid,
  );

  useEffect(() => {
    setLocallyHiddenUids(new Set());
  }, [kind, profileUid]);

  useEffect(() => {
    if (!visible) {
      setSearchText('');
      setDebouncedSearchText('');
      setBotUnfollowAction(null);
      setLocallyHiddenUids(new Set());
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 250);

    return () => clearTimeout(timeout);
  }, [searchText, visible]);

  function loadMoreConnections() {
    if (
      connectionsQuery.hasNextPage &&
      !isSearching &&
      !connectionsQuery.isLoading &&
      !connectionsQuery.isFetchingNextPage
    ) {
      void connectionsQuery.fetchNextPage();
    }
  }

  function handleConnectionChange({
    following,
    uid,
  }: {
    following: boolean;
    uid: string;
  }) {
    if (kind !== 'following') {
      return;
    }

    setLocallyHiddenUids((currentHiddenUids) => {
      const nextHiddenUids = new Set(currentHiddenUids);

      if (following) {
        nextHiddenUids.delete(uid);
      } else {
        nextHiddenUids.add(uid);
      }

      return nextHiddenUids;
    });
  }

  return (
    <>
      <ModalShell title={title} visible={visible} onClose={onClose}>
        <FlatList
          contentContainerStyle={styles.connectionListContent}
          data={visibleConnections}
          keyboardShouldPersistTaps="handled"
          keyExtractor={(item) => item.uid}
          ListEmptyComponent={
            connectionsQuery.isLoading ? (
              <View style={styles.centerState}>
                <ActivityIndicator color={colors.primary} />
                <Text style={styles.mutedText}>
                  Loading {title.toLowerCase()}...
                </Text>
              </View>
            ) : connectionsQuery.isError && !isSearching ? (
              <View style={styles.centerState}>
                <Text style={styles.errorText}>
                  Unable to load {title.toLowerCase()}.
                </Text>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    void connectionsQuery.refetch();
                  }}
                  style={styles.retryButton}
                >
                  <Text style={styles.retryText}>Try again</Text>
                </Pressable>
              </View>
            ) : searchQuery.isError && isSearching ? (
              <View style={styles.centerState}>
                <Text style={styles.errorText}>
                  Unable to search {title.toLowerCase()}.
                </Text>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    void searchQuery.refetch();
                  }}
                  style={styles.retryButton}
                >
                  <Text style={styles.retryText}>Try again</Text>
                </Pressable>
              </View>
            ) : isSearchLoading ? (
              <View style={styles.centerState}>
                <ActivityIndicator color={colors.primary} />
                <Text style={styles.mutedText}>
                  Searching all {connectionLabel}...
                </Text>
              </View>
            ) : loadedConnections.length === 0 && !isSearching ? (
              <View style={styles.centerState}>
                <Text style={styles.mutedText}>{emptyText}</Text>
              </View>
            ) : (
              <View style={styles.centerState}>
                <Text style={styles.mutedText}>
                  No matches in all {connectionLabel}.
                </Text>
                {isSearching ? (
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => setSearchText('')}
                    style={styles.retryButton}
                  >
                    <Text style={styles.retryText}>Clear search</Text>
                  </Pressable>
                ) : null}
              </View>
            )
          }
          ListFooterComponent={
            !isSearching &&
            (connectionsQuery.hasNextPage ||
              connectionsQuery.isFetchingNextPage) ? (
              <View style={styles.connectionFooter}>
                {connectionsQuery.isFetchingNextPage ? (
                  <ActivityIndicator color={colors.primary} />
                ) : (
                  <Pressable
                    accessibilityRole="button"
                    onPress={loadMoreConnections}
                    style={styles.loadMoreButton}
                  >
                    <Text style={styles.loadMoreText}>Load more</Text>
                  </Pressable>
                )}
              </View>
            ) : null
          }
          ListHeaderComponent={
            <View style={styles.searchHeader}>
              <TextInput
                accessibilityLabel={`Search ${connectionLabel}`}
                autoCapitalize="none"
                autoCorrect={false}
                clearButtonMode="while-editing"
                onChangeText={setSearchText}
                placeholder={`Search ${connectionLabel}`}
                placeholderTextColor={colors.mutedText}
                returnKeyType="search"
                selectionColor={colors.primary}
                style={styles.searchInput}
                value={searchText}
              />
              <View style={styles.searchMetaRow}>
                {searchStatus ? (
                  <Text style={styles.searchMetaPrimary}>{searchStatus}</Text>
                ) : null}
                <View style={styles.searchMetaPill}>
                  <Text style={styles.searchMetaPillText}>
                    {isSearching ? searchScopeStatus : loadedStatus}
                  </Text>
                </View>
                {isSearching ? (
                  <Text style={styles.searchMetaHint}>
                    Searches names and usernames
                  </Text>
                ) : connectionsQuery.hasNextPage ? (
                  <Text style={styles.searchMetaHint}>Scroll to load more</Text>
                ) : null}
              </View>
            </View>
          }
          onEndReached={isSearching ? undefined : loadMoreConnections}
          onEndReachedThreshold={0.35}
          renderItem={({ item }) => (
            <ConnectionRow
              currentUid={currentUid}
              isOwnProfile={isOwnProfile}
              item={item}
              kind={kind}
              onChanged={handleConnectionChange}
              onClose={onClose}
              onRequestBotUnfollow={(action) => {
                setBotUnfollowAction(() => action);
              }}
            />
          )}
          stickyHeaderIndices={[0]}
          style={styles.connectionList}
        />
      </ModalShell>
      <BotUnfollowConfirmModal
        visible={Boolean(botUnfollowAction)}
        onCancel={() => setBotUnfollowAction(null)}
        onConfirm={() => {
          botUnfollowAction?.();
          setBotUnfollowAction(null);
        }}
      />
    </>
  );
}

export function ProfileReactionBreakdownModal({
  onClose,
  stats,
  visible,
}: {
  onClose: () => void;
  stats: ProfileStats;
  visible: boolean;
}) {
  const reactionCounts = {
    ...emptyProfileReactionCounts(),
    ...(stats.reactionCounts ?? {}),
  };
  const totalLikeCount = totalProfileReactions({ reactionCounts });
  const chartStats = { ...stats, reactionCounts, totalLikeCount };

  return (
    <ModalShell title="Reaction breakdown" visible={visible} onClose={onClose}>
      <ReactionPieChart stats={chartStats} />
      <View style={styles.legend}>
        {REACTION_ORDER.map((reaction) => {
          const meta = REACTION_META[reaction];
          const count = reactionCounts[reaction] ?? 0;

          return (
            <View key={reaction} style={styles.legendRow}>
              <View
                style={[styles.legendDot, { backgroundColor: meta.color }]}
              />
              <Text style={styles.legendEmoji}>{meta.emoji}</Text>
              <Text style={styles.legendLabel}>{meta.label}</Text>
              <Text style={styles.legendCount}>{count}</Text>
            </View>
          );
        })}
      </View>
    </ModalShell>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(22, 13, 50, 0.42)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.sm,
  },
  centerState: {
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.xl,
  },
  chartCenter: {
    alignItems: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 66,
  },
  chartLabel: {
    color: colors.mutedText,
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  chartTotal: {
    color: colors.text,
    fontSize: 24,
    fontWeight: '900',
  },
  chartWrap: {
    alignItems: 'center',
    alignSelf: 'center',
    height: 180,
    justifyContent: 'center',
    marginTop: spacing.md,
    position: 'relative',
    width: 180,
  },
  closeButton: {
    alignItems: 'center',
    borderRadius: 21,
    height: 42,
    justifyContent: 'center',
    width: 42,
  },
  compactActionButton: {
    alignItems: 'center',
    borderRadius: 999,
    borderWidth: 1,
    justifyContent: 'center',
    minHeight: 34,
    paddingHorizontal: spacing.md,
    paddingVertical: 7,
  },
  compactActionButtonDisabled: {
    opacity: 0.55,
  },
  compactActionText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
  },
  confirmActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'flex-end',
  },
  confirmBackdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(22, 13, 50, 0.5)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  confirmSheet: {
    backgroundColor: colors.surface,
    borderRadius: 16,
    gap: spacing.md,
    maxWidth: 420,
    padding: spacing.lg,
    width: '100%',
  },
  confirmText: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 21,
  },
  confirmTitle: {
    color: colors.text,
    fontSize: 19,
    fontWeight: '900',
  },
  connectionList: {
    maxHeight: 420,
  },
  connectionListContent: {
    flexGrow: 1,
  },
  connectionFooter: {
    alignItems: 'center',
    borderTopColor: colors.border,
    borderTopWidth: StyleSheet.hairlineWidth,
    padding: spacing.md,
  },
  connectionIdentity: {
    flex: 1,
  },
  connectionRow: {
    alignItems: 'center',
    borderTopColor: colors.border,
    borderTopWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: spacing.md,
    minHeight: 68,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  connectionRowPressed: {
    backgroundColor: colors.surfaceTint,
  },
  dangerActionButton: {
    backgroundColor: colors.danger,
    borderColor: colors.danger,
  },
  errorText: {
    color: colors.danger,
    fontSize: 14,
    fontWeight: '800',
    textAlign: 'center',
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  headerSpacer: {
    height: 42,
    width: 42,
  },
  followingPill: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing.sm,
    paddingVertical: 7,
  },
  followingPillText: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
  },
  legend: {
    gap: spacing.sm,
    padding: spacing.lg,
    paddingTop: spacing.md,
  },
  legendCount: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  legendDot: {
    borderRadius: 6,
    height: 12,
    width: 12,
  },
  legendEmoji: {
    fontSize: 17,
    width: 26,
  },
  legendLabel: {
    color: colors.text,
    flex: 1,
    fontSize: 15,
    fontWeight: '800',
  },
  legendRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    minHeight: 30,
  },
  mutedText: {
    color: colors.mutedText,
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
  },
  retryButton: {
    backgroundColor: colors.primary,
    borderRadius: 999,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  retryText: {
    color: colors.surface,
    fontSize: 14,
    fontWeight: '900',
  },
  primaryActionButton: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  primaryActionButtonPressed: {
    backgroundColor: colors.primaryPressed,
    borderColor: colors.primaryPressed,
  },
  secondaryActionButton: {
    backgroundColor: colors.surface,
    borderColor: colors.secondary,
  },
  secondaryActionButtonPressed: {
    backgroundColor: colors.surfaceTint,
  },
  secondaryActionButtonText: {
    color: colors.secondary,
  },
  loadMoreButton: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  loadMoreText: {
    color: colors.secondary,
    fontSize: 14,
    fontWeight: '900',
  },
  searchHeader: {
    backgroundColor: colors.surface,
    borderBottomColor: colors.border,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: spacing.xs,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  searchInput: {
    backgroundColor: colors.inputBackground,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    color: colors.text,
    fontSize: 15,
    minHeight: 44,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  searchMetaHint: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
  },
  searchMetaPill: {
    alignSelf: 'flex-start',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
  },
  searchMetaPillText: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
  },
  searchMetaPrimary: {
    color: colors.text,
    fontSize: 12,
    fontWeight: '900',
  },
  searchMetaRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  sheet: {
    backgroundColor: colors.surface,
    borderRadius: 18,
    maxHeight: '82%',
    maxWidth: 520,
    overflow: 'hidden',
    width: '100%',
  },
  title: {
    color: colors.text,
    flex: 1,
    fontSize: 18,
    fontWeight: '900',
    textAlign: 'center',
  },
});
